import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  LocalFireDepartment,
  CheckCircle,
  Quiz,
  Assignment,
  Timer,
} from '@mui/icons-material';
import gamificationService, { StudentRating } from '../../services/gamificationService';

interface StudentRatingCardProps {
  studentId?: number;
}

const StudentRatingCard: React.FC<StudentRatingCardProps> = ({ studentId }) => {
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<StudentRating | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRating();
  }, [studentId]);

  const loadRating = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await gamificationService.getStudentRating();
      setRating(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load rating');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!rating) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No rating data available.</Alert>
        </CardContent>
      </Card>
    );
  }

  // Calculate progress to next rank (simplified - would need rank definitions)
  const progressToNextRank = rating.rankTier < 10 ? ((rating.rating % 100) / 100) * 100 : 100;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              mr: 2,
            }}
          >
            <EmojiEvents sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {rating.rankName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rating: {rating.rating.toFixed(0)} (Peak: {rating.peakRating.toFixed(0)})
            </Typography>
          </Box>
        </Box>

        {rating.rankTier < 10 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Progress to Next Rank
            </Typography>
            <LinearProgress variant="determinate" value={progressToNextRank} sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <LocalFireDepartment color="error" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{rating.currentStreakDays}</Typography>
              <Typography variant="caption" color="text.secondary">
                Day Streak
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <TrendingUp color="primary" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{rating.totalPoints.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total Points
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="h6">{rating.topicsCompletedCount}</Typography>
              <Typography variant="caption" color="text.secondary">
                Topics Completed
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Chip
              icon={<Quiz />}
              label={`${rating.quizzesPassedCount} Quizzes Passed`}
              variant="outlined"
              sx={{ mb: 1, width: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip
              icon={<Assignment />}
              label={`${rating.assessmentsPassedCount} Assessments Passed`}
              variant="outlined"
              sx={{ mb: 1, width: '100%' }}
            />
          </Grid>
          {rating.globalRank && (
            <Grid item xs={12} sm={4}>
              <Chip
                label={`Global Rank: #${rating.globalRank}`}
                color="primary"
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
          {rating.schoolRank && (
            <Grid item xs={12} sm={4}>
              <Chip
                label={`School Rank: #${rating.schoolRank}`}
                color="secondary"
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
          {rating.classRank && (
            <Grid item xs={12} sm={4}>
              <Chip
                label={`Class Rank: #${rating.classRank}`}
                color="success"
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default StudentRatingCard;
